/**
 * Bingo engine — the pure, variant-agnostic gameplay core.
 *
 * 75-ball US Bingo. A 5×5 card; columns are B[1–15] I[16–30] N[31–45] G[46–60]
 * O[61–75], with a FREE space in the dead centre. The auto-caller draws numbers
 * from a daily-seeded shuffle of 1..75; the player daubs the called number on
 * their card(s). Win is a completed PATTERN.
 *
 * Everything here is pure — no React, no DOM, no audio, no timers. The caller's
 * interval, the audio, the haptics all live in the renderer; this module only
 * knows about numbers, cards, daubs, and which patterns are complete. That
 * makes the social promise — "the same game for everyone today" — testable as a
 * plain function of the seed.
 *
 * Design notes a 75-ball implementation can get subtly wrong, handled here:
 *   - The FREE centre (row 2, col 2 — index 12) is ALWAYS daubed and never
 *     callable. Win detection treats it as permanently marked.
 *   - Each column draws its 5 numbers from that column's 15-number band WITHOUT
 *     replacement, so a card never repeats a number.
 *   - A number's letter is a function of the number alone (1–15 → B, …), not of
 *     where it lands on a card.
 */
import { mulberry32, shuffle } from './rng.js';
// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------
export const COLUMNS = ['B', 'I', 'N', 'G', 'O'];
export const CARD_SIZE = 5;
/** Flat index of the FREE centre cell (row 2, col 2). */
export const FREE_INDEX = 12;
/** Numbers per column band (B = 1..15, I = 16..30, …). */
export const BAND_SIZE = 15;
export const BALL_MIN = 1;
export const BALL_MAX = 75;
/** Max cards a player may hold at once. */
export const MAX_CARDS = 4;
// ---------------------------------------------------------------------------
// Numbers ↔ letters
// ---------------------------------------------------------------------------
/**
 * The column a ball belongs to. 1–15 → B, 16–30 → I, … 61–75 → O. Throws on
 * out-of-range input rather than silently clamping — a bad number is a bug.
 */
export function columnForNumber(n) {
    if (!Number.isInteger(n) || n < BALL_MIN || n > BALL_MAX) {
        throw new Error(`columnForNumber: ${n} is not a valid bingo number`);
    }
    return COLUMNS[Math.floor((n - 1) / BAND_SIZE)];
}
/** Human-readable call, e.g. `B 7`, `N 42`. Used everywhere the call is shown. */
export function callLabel(n) {
    return `${columnForNumber(n)} ${n}`;
}
/** The inclusive [min, max] band a column draws from. */
export function bandFor(letter) {
    const col = COLUMNS.indexOf(letter);
    const min = col * BAND_SIZE + 1;
    return { min, max: min + BAND_SIZE - 1 };
}
/**
 * Build one valid 75-ball card from a seed. Each column draws 5 distinct numbers
 * from its band without replacement; the centre is FREE (`null`).
 *
 * Deterministic: same seed → same card. The `cardOffset` lets a hand of several
 * cards stay distinct while all deriving from the one game seed (card 0, 1, 2…).
 */
export function makeCard(seed, cardOffset = 0) {
    // Mix the card index into the seed so each card in a hand is different but the
    // whole hand still derives deterministically from the game seed.
    const rng = mulberry32((seed ^ Math.imul(cardOffset + 1, 0x9e3779b1)) >>> 0);
    const cells = new Array(CARD_SIZE * CARD_SIZE).fill(null);
    for (let col = 0; col < CARD_SIZE; col += 1) {
        const letter = COLUMNS[col];
        const { min } = bandFor(letter);
        const band = Array.from({ length: BAND_SIZE }, (_, i) => min + i);
        const drawn = shuffle(band, rng).slice(0, CARD_SIZE);
        for (let row = 0; row < CARD_SIZE; row += 1) {
            const idx = row * CARD_SIZE + col;
            cells[idx] = drawn[row];
        }
    }
    cells[FREE_INDEX] = null; // FREE centre
    return cells;
}
/** Build a hand of `count` distinct cards (1..MAX_CARDS) from one game seed. */
export function makeHand(seed, count) {
    const n = Math.max(1, Math.min(MAX_CARDS, Math.floor(count)));
    return Array.from({ length: n }, (_, i) => makeCard(seed, i));
}
/** Validate a card structurally — used in tests, cheap enough to keep around. */
export function isValidCard(card) {
    if (card.length !== CARD_SIZE * CARD_SIZE)
        return false;
    if (card[FREE_INDEX] !== null)
        return false;
    const seen = new Set();
    for (let col = 0; col < CARD_SIZE; col += 1) {
        const { min, max } = bandFor(COLUMNS[col]);
        for (let row = 0; row < CARD_SIZE; row += 1) {
            const idx = row * CARD_SIZE + col;
            if (idx === FREE_INDEX)
                continue;
            const v = card[idx];
            if (typeof v !== 'number' || v < min || v > max)
                return false;
            if (seen.has(v))
                return false;
            seen.add(v);
        }
    }
    return true;
}
// ---------------------------------------------------------------------------
// The call sequence
// ---------------------------------------------------------------------------
/**
 * The full draw order for a game: a shuffled permutation of 1..75. The caller
 * walks this list one number at a time. Same seed → same order, so everyone in
 * a community hears the same calls in the same order today.
 */
export function makeCallSequence(seed) {
    const balls = Array.from({ length: BALL_MAX }, (_, i) => i + 1);
    return shuffle(balls, mulberry32(seed >>> 0));
}
export const PATTERNS = [
    {
        id: 'line',
        title: 'Any line',
        blurb: 'Five in a row — across, down, or corner to corner.',
    },
    {
        id: 'four-corners',
        title: 'Four corners',
        blurb: 'The four corner squares of the card.',
    },
    {
        id: 'blackout',
        title: 'Full card',
        blurb: 'Every square on the card. The long game.',
    },
];
export function patternInfo(id) {
    return PATTERNS.find((p) => p.id === id) ?? PATTERNS[0];
}
const CORNERS = [0, 4, 20, 24];
/**
 * Every "line" — 5 rows, 5 columns, 2 diagonals — as cell-index lists. The FREE
 * centre falls on the middle row, middle column, and both diagonals, which is
 * exactly why those lines are a little easier (it's always marked).
 */
function lineGroups() {
    const groups = [];
    for (let r = 0; r < CARD_SIZE; r += 1) {
        groups.push(Array.from({ length: CARD_SIZE }, (_, c) => r * CARD_SIZE + c));
    }
    for (let c = 0; c < CARD_SIZE; c += 1) {
        groups.push(Array.from({ length: CARD_SIZE }, (_, r) => r * CARD_SIZE + c));
    }
    groups.push(Array.from({ length: CARD_SIZE }, (_, i) => i * CARD_SIZE + i));
    groups.push(Array.from({ length: CARD_SIZE }, (_, i) => i * CARD_SIZE + (CARD_SIZE - 1 - i)));
    return groups;
}
const LINE_GROUPS = lineGroups();
export function initialDaubs() {
    const d = new Array(CARD_SIZE * CARD_SIZE).fill(false);
    d[FREE_INDEX] = true; // FREE is always marked
    return d;
}
function isMarked(daubs, idx) {
    return idx === FREE_INDEX || daubs[idx] === true;
}
/**
 * Which cell-index groups are complete for a pattern. For `line` this can be
 * several at once (handy for highlighting every winning line). For four-corners
 * / blackout there is a single group.
 *
 * Returned groups are the actual winning cell sets, so the renderer can light
 * them up without re-deriving the geometry.
 */
export function winningGroups(daubs, pattern) {
    switch (pattern) {
        case 'line':
            return LINE_GROUPS.filter((g) => g.every((i) => isMarked(daubs, i)));
        case 'four-corners':
            return CORNERS.every((i) => isMarked(daubs, i)) ? [CORNERS] : [];
        case 'blackout': {
            const all = Array.from({ length: CARD_SIZE * CARD_SIZE }, (_, i) => i);
            return all.every((i) => isMarked(daubs, i)) ? [all] : [];
        }
    }
}
/** True when the daubs satisfy the chosen pattern on this card. */
export function isWinningCard(daubs, pattern) {
    return winningGroups(daubs, pattern).length > 0;
}
/**
 * How many more correctly-daubed cells are needed to complete the pattern — the
 * minimum over all candidate groups. Used to drive the gentle "one away" nudge
 * and the near-win screenshot state. 0 means already won.
 */
export function cellsToWin(daubs, pattern) {
    const groups = pattern === 'line'
        ? LINE_GROUPS
        : pattern === 'four-corners'
            ? [CORNERS]
            : [Array.from({ length: CARD_SIZE * CARD_SIZE }, (_, i) => i)];
    let best = Infinity;
    for (const g of groups) {
        const missing = g.reduce((n, i) => n + (isMarked(daubs, i) ? 0 : 1), 0);
        if (missing < best)
            best = missing;
    }
    return best === Infinity ? 0 : best;
}
export function initialState(setup) {
    const cards = makeHand(setup.seed, setup.cardCount);
    return {
        mode: setup.mode,
        pattern: setup.pattern,
        phase: 'playing',
        sequence: makeCallSequence(setup.seed),
        calledCount: 0,
        cards,
        daubs: cards.map(() => initialDaubs()),
        autoDaub: setup.autoDaub ?? false,
    };
}
/** The set of numbers called so far (the first `calledCount` of the sequence). */
export function calledNumbers(state) {
    return new Set(state.sequence.slice(0, state.calledCount));
}
/** The most recent call, or null before the first call. */
export function currentCall(state) {
    if (state.calledCount === 0)
        return null;
    return state.sequence[state.calledCount - 1] ?? null;
}
/** True when every number has been drawn (the caller has nothing left). */
export function isSequenceExhausted(state) {
    return state.calledCount >= state.sequence.length;
}
function anyCardWins(state) {
    return state.daubs.some((d) => isWinningCard(d, state.pattern));
}
export function reduce(state, action) {
    switch (action.kind) {
        case 'call': {
            if (state.phase !== 'playing')
                return state;
            if (isSequenceExhausted(state))
                return state;
            const calledCount = state.calledCount + 1;
            let daubs = state.daubs;
            // Auto-daub: mark the just-called number on every card that holds it.
            if (state.autoDaub) {
                const justCalled = state.sequence[calledCount - 1];
                daubs = state.cards.map((card, ci) => {
                    const idx = card.indexOf(justCalled);
                    if (idx === -1 || state.daubs[ci][idx])
                        return state.daubs[ci];
                    const copy = state.daubs[ci].slice();
                    copy[idx] = true;
                    return copy;
                });
            }
            const next = { ...state, calledCount, daubs };
            if (anyCardWins(next))
                next.phase = 'won';
            return next;
        }
        case 'daub': {
            const { cardIndex, cellIndex } = action;
            const card = state.cards[cardIndex];
            if (!card)
                return state;
            if (cellIndex < 0 || cellIndex >= CARD_SIZE * CARD_SIZE)
                return state;
            if (cellIndex === FREE_INDEX)
                return state; // FREE is fixed
            const value = card[cellIndex];
            if (typeof value !== 'number')
                return state;
            // A cell is only markable once its number has actually been called —
            // daubing ahead of the caller isn't a real bingo. (Missing a call is
            // never penalised: the number stays markable for the rest of the game.)
            if (!calledNumbers(state).has(value))
                return state;
            const cardDaubs = state.daubs[cardIndex];
            const copy = cardDaubs.slice();
            copy[cellIndex] = !copy[cellIndex]; // toggle — tapping again un-marks
            const daubs = state.daubs.map((d, i) => (i === cardIndex ? copy : d));
            const next = { ...state, daubs };
            if (anyCardWins(next))
                next.phase = 'won';
            return next;
        }
        case 'setPattern': {
            if (action.pattern === state.pattern)
                return state;
            const next = { ...state, pattern: action.pattern };
            // Switching pattern can retroactively make an existing board a winner
            // (e.g. you had a line while playing blackout). Re-evaluate.
            next.phase = anyCardWins(next) ? 'won' : 'playing';
            return next;
        }
        case 'setAutoDaub': {
            if (action.on === state.autoDaub)
                return state;
            if (!action.on)
                return { ...state, autoDaub: false };
            // Turning auto-daub ON retroactively marks every already-called number.
            const called = calledNumbers(state);
            const daubs = state.cards.map((card, ci) => {
                const copy = state.daubs[ci].slice();
                for (let i = 0; i < card.length; i += 1) {
                    const v = card[i];
                    if (typeof v === 'number' && called.has(v))
                        copy[i] = true;
                }
                return copy;
            });
            const next = { ...state, autoDaub: true, daubs };
            if (anyCardWins(next))
                next.phase = 'won';
            return next;
        }
        case 'reset':
            return initialState(action.setup);
    }
}
/**
 * Cells on a card whose number has been called but the player hasn't daubed yet
 * — the "you missed one" set. Drives the gentle nudge highlight. FREE excluded.
 */
export function missedCells(state, cardIndex) {
    const card = state.cards[cardIndex];
    const daubs = state.daubs[cardIndex];
    if (!card || !daubs)
        return [];
    const called = calledNumbers(state);
    const out = [];
    for (let i = 0; i < card.length; i += 1) {
        const v = card[i];
        if (typeof v === 'number' && called.has(v) && !daubs[i])
            out.push(i);
    }
    return out;
}
//# sourceMappingURL=engine.js.map